import { PulseStorage, deepObjectEqual, getAllQueryParamsInURL } from '@pulse/common';
import { Observable, ReplaySubject } from 'rxjs';
import { ClickId, Referrer, TrafficSource } from '../../models';
import { PageInfoService } from '../page-info.service';
import { UTMTrackerService } from '../utm.service';

export class TrafficSourceService {
    private trafficSourceSubject$ = new ReplaySubject<TrafficSource | null>(1);

    constructor(
        private utmService: UTMTrackerService,
        private storage: PulseStorage,
        private page: PageInfoService,
    ) {
        // Emit initial traffic source if any exists
        const initialTrafficSource = this.getStoredTrafficSource();

        if (initialTrafficSource) {
            this.trafficSourceSubject$.next(initialTrafficSource);
        }
    }

    getTrafficSource(): TrafficSource | null {
        const utmParams = this.utmService.getUTMParams();
        const referrer = this.getReferrer();
        const clickId = this.getClickId(referrer?.url);
        const landingPageURL = this.page.landingPageUrl();

        if (!(utmParams || clickId || referrer)) {
            return null;
        }

        return {
            utmParams,
            clickId,
            referrer,
            landingPageURL,
        };
    }

    getLastTrafficSource(): TrafficSource | null {
        const previous = this.getStoredTrafficSource();
        const current = this.getTrafficSource();
        const internal = current?.referrer?.internal ?? false;

        const changed =
            this.changed(current?.clickId, previous?.clickId) ||
            this.changed(current?.utmParams, previous?.utmParams) ||
            (this.changed(current?.referrer, previous?.referrer) && !internal);

        if (changed) {
            this.storage.setJSON('traffic_source', current);
            // Emit the new traffic source to subscribers
            this.trafficSourceSubject$.next(current);

            return current;
        }

        return previous;
    }

    /**
     * Get an observable that emits traffic source changes.
     * Emits the current traffic source when subscribed and whenever it changes.
     */
    getLastTrafficSource$(): Observable<TrafficSource | null> {
        return this.trafficSourceSubject$.asObservable();
    }

    private getStoredTrafficSource(): TrafficSource | null {
        return this.storage.getJSON<TrafficSource>('traffic_source');
    }

    private getClickId(referrer?: string): ClickId | undefined {
        const { searchParams } = new URL(getAllQueryParamsInURL(this.page.landingPageUrl()));
        const googleClickId = searchParams.get('gclid');
        const googleClickSource =
            searchParams.get('gclsrc') || searchParams.get('wbraid') || searchParams.get('gbraid');
        const microsoftClickId = searchParams.get('msclkid');
        const fbClickId = searchParams.get('fbclid');
        const yandexOrYahooClickId = searchParams.get('yclid');
        const yandexOrYahooClickSource = referrer?.includes('yahoo') ? 'yahoo' : 'yandex';
        const doubleClickId = searchParams.get('dclid');
        const linkedinClickId = searchParams.get('li_fat_id');
        const tiktokClickId = searchParams.get('ttclid');
        const twitterClickId = searchParams.get('twclid');

        let clickId: string | undefined;
        let clickSource: string | undefined;

        if (googleClickId) {
            clickId = googleClickId;
            clickSource = googleClickSource ? `google.${googleClickSource}` : 'google';
        } else if (fbClickId) {
            clickId = fbClickId;
            clickSource = 'facebook';
        } else if (microsoftClickId) {
            clickId = microsoftClickId;
            clickSource = 'microsoft';
        } else if (yandexOrYahooClickId) {
            clickId = yandexOrYahooClickId;
            clickSource = yandexOrYahooClickSource;
        } else if (doubleClickId) {
            clickId = doubleClickId;
            clickSource = 'doubleclick';
        } else if (linkedinClickId) {
            clickId = linkedinClickId;
            clickSource = 'linkedin';
        } else if (tiktokClickId) {
            clickId = tiktokClickId;
            clickSource = 'tiktok';
        } else if (twitterClickId) {
            clickId = twitterClickId;
            clickSource = 'twitter';
        }

        return clickId && clickSource ? { value: clickId, source: clickSource } : undefined;
    }

    private getReferrer(): Referrer | undefined {
        const referrer = this.page.referrer() || '';

        if (referrer) {
            return {
                url: referrer,
                internal: this.isInternalURL(referrer),
            };
        }

        return undefined;
    }

    private isInternalURL(referrer: string): boolean {
        return new URL(referrer).host === this.page.url().host;
    }

    private changed(current: any, previous: any) {
        return current && !deepObjectEqual(current, previous);
    }
}
